import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  createDefaultCalculationRules,
  createDefaultOutcomeBranches,
  evaluateCalculationRules,
} from '@/lib/form-calculation'
import { evaluateCondition, evaluateLogicJump, getNextQuestionId } from '@/lib/logic-evaluator'
import type { AnswerValue, LogicJump, Question, QuestionGroupReference } from '@/types/form'
import type { CalculationResult, LogicSimulationWarning } from '@/types/form-variables'
import { AlertTriangle, CheckCircle2, GitBranch, Route, Variable } from 'lucide-react'
import { useMemo, useState } from 'react'

type SimulationRuleResult = {
  jump: LogicJump
  matched: boolean
}

export function LogicSimulationDialog({
  open,
  onOpenChange,
  questions,
  questionGroups = [],
  logicJumps,
  hiddenFields,
  selectedQuestionId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  questions: Question[]
  questionGroups?: QuestionGroupReference[]
  logicJumps: LogicJump[]
  hiddenFields: Record<string, string>
  selectedQuestionId: string | null
}) {
  const [startQuestionId, setStartQuestionId] = useState(
    selectedQuestionId ?? questions[0]?.id ?? '',
  )
  const [draftAnswers, setDraftAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      questions.map((question) => [question.id, defaultAnswerForQuestion(question)]),
    ),
  )
  const [draftHiddenFields, setDraftHiddenFields] = useState(hiddenFields)

  const answers = useMemo(
    () =>
      new Map<string, AnswerValue>(
        questions.map((question) => [
          question.id,
          parseAnswer(question, draftAnswers[question.id]),
        ]),
      ),
    [draftAnswers, questions],
  )
  const calculations = useMemo(() => createDefaultCalculationRules(questions), [questions])
  const calculationResults = useMemo(
    () => evaluateCalculationRules(calculations, questions, answers),
    [answers, calculations, questions],
  )
  const calculatedHiddenFields = useMemo(
    () => ({
      ...draftHiddenFields,
      ...Object.fromEntries(calculationResults.map((result) => [result.key, String(result.value)])),
    }),
    [calculationResults, draftHiddenFields],
  )
  const ruleResults = useMemo<SimulationRuleResult[]>(
    () =>
      logicJumps
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((jump) => ({
          jump,
          matched: evaluateLogicJump(jump, answers, calculatedHiddenFields),
        })),
    [answers, calculatedHiddenFields, logicJumps],
  )
  const path = useMemo(
    () =>
      simulatePath(
        startQuestionId,
        questions,
        questionGroups,
        logicJumps,
        answers,
        calculatedHiddenFields,
      ),
    [answers, calculatedHiddenFields, logicJumps, questionGroups, questions, startQuestionId],
  )
  const warnings = useMemo(
    () => analyzeLogicWarnings(questions, questionGroups, logicJumps, ruleResults, path),
    [logicJumps, path, questionGroups, questions, ruleResults],
  )
  const outcome = useMemo(
    () => matchOutcome(calculationResults, calculatedHiddenFields),
    [calculationResults, calculatedHiddenFields],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0">
        <div className="flex h-full min-h-0 flex-col bg-slate-50">
          <div className="shrink-0 border-b border-slate-200 bg-white p-5 pr-14">
            <DialogHeader>
              <DialogTitle>邏輯模擬測試器</DialogTitle>
              <DialogDescription>
                使用前端 mock-safe 答案測試跳題路徑、命中規則、變數計算與結果頁分流。
              </DialogDescription>
            </DialogHeader>
          </div>

          <ScrollArea className="min-h-0 flex-1">
            <div className="grid gap-5 p-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(420px,0.9fr)]">
              <div className="space-y-5">
                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <Route className="size-4 text-indigo-600" />
                    模擬答案
                  </div>
                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label>起始問題</Label>
                      <Select value={startQuestionId} onValueChange={setStartQuestionId}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {questions.map((question) => (
                            <SelectItem key={question.id} value={question.id}>
                              {question.orderIndex + 1}. {question.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      {questions.map((question) => (
                        <AnswerEditor
                          key={question.id}
                          question={question}
                          value={draftAnswers[question.id] ?? ''}
                          onChange={(value) =>
                            setDraftAnswers((current) => ({ ...current, [question.id]: value }))
                          }
                        />
                      ))}
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <Variable className="size-4 text-indigo-600" />
                    Hidden fields / 變數輸入
                  </div>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {Object.entries(draftHiddenFields).map(([key, value]) => (
                      <div key={key} className="space-y-2">
                        <Label>{key}</Label>
                        <Input
                          value={value}
                          onChange={(event) =>
                            setDraftHiddenFields((current) => ({
                              ...current,
                              [key]: event.currentTarget.value,
                            }))
                          }
                        />
                      </div>
                    ))}
                    {Object.keys(draftHiddenFields).length === 0 ? (
                      <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                        此問卷目前沒有 hidden field。計算變數會由模擬答案自動產生。
                      </div>
                    ) : null}
                  </div>
                </section>
              </div>

              <div className="space-y-5">
                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <GitBranch className="size-4 text-indigo-600" />
                    預期路徑
                  </div>
                  <div className="mt-4 space-y-2">
                    {path.map((questionId) => {
                      const question = questions.find((item) => item.id === questionId)
                      return (
                        <div
                          key={questionId}
                          className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2"
                        >
                          <span className="text-sm font-medium text-slate-950">
                            {(question?.orderIndex ?? 0) + 1}. {question?.title ?? '結束問卷'}
                          </span>
                          <Badge variant="secondary">
                            {question ? `第 ${question.orderIndex + 1} 題` : '完成頁'}
                          </Badge>
                        </div>
                      )
                    })}
                  </div>
                </section>

                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <CheckCircle2 className="size-4 text-indigo-600" />
                    命中規則
                  </div>
                  <div className="mt-4 space-y-2">
                    {ruleResults.map(({ jump, matched }) => (
                      <div
                        key={jump.id}
                        className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>規則 {jump.order + 1}</span>
                          <Badge variant={matched ? 'success' : 'secondary'}>
                            {matched ? '命中' : '未命中'}
                          </Badge>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          來源：{questionTitle(jump.questionId, questions)} →{' '}
                          {jumpTargetTitle(jump, questions, questionGroups)}
                        </div>
                      </div>
                    ))}
                    {ruleResults.length === 0 ? (
                      <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                        尚未建立邏輯規則。
                      </div>
                    ) : null}
                  </div>
                </section>

                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <Variable className="size-4 text-indigo-600" />
                    變數計算結果
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {calculationResults.map((result) => (
                      <div key={result.key} className="rounded-md bg-indigo-50 px-3 py-2">
                        <div className="text-xs text-indigo-600">{result.label}</div>
                        <div className="mt-1 font-semibold text-indigo-950">
                          {result.formattedValue}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600">
                    結果頁分流：<span className="font-medium text-slate-950">{outcome.label}</span>
                  </div>
                </section>

                <section className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <AlertTriangle className="size-4 text-amber-600" />
                    診斷警告
                  </div>
                  <div className="mt-4 space-y-2">
                    {warnings.map((warning) => (
                      <div
                        key={`${warning.code}-${warning.label}`}
                        className="rounded-md bg-amber-50 px-3 py-2"
                      >
                        <div className="text-sm font-medium text-amber-900">{warning.label}</div>
                        <div className="mt-1 text-xs leading-5 text-amber-700">
                          {warning.description}
                        </div>
                      </div>
                    ))}
                    {warnings.length === 0 ? (
                      <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                        目前沒有偵測到循環、未設定目標或衝突規則。
                      </div>
                    ) : null}
                  </div>
                </section>
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AnswerEditor({
  question,
  value,
  onChange,
}: {
  question: Question
  value: string
  onChange: (value: string) => void
}) {
  if (question.choices.length > 0) {
    return (
      <div className="space-y-2">
        <Label>{question.title}</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="選擇答案" />
          </SelectTrigger>
          <SelectContent>
            {question.choices.map((choice) => (
              <SelectItem key={choice.id} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (question.type === 'YES_NO') {
    return (
      <div className="space-y-2">
        <Label>{question.title}</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">是</SelectItem>
            <SelectItem value="false">否</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label>{question.title}</Label>
      <Input
        value={value}
        type={
          ['NUMBER', 'NUMBER_SLIDER', 'TOTAL', 'NPS', 'RATING'].includes(question.type)
            ? 'number'
            : 'text'
        }
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder="輸入模擬答案"
      />
    </div>
  )
}

function simulatePath(
  startQuestionId: string,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
  logicJumps: LogicJump[],
  answers: Map<string, AnswerValue>,
  hiddenFields: Record<string, string>,
) {
  const path: string[] = []
  let currentId: string | null = startQuestionId
  const visited = new Set<string>()
  const maxSteps = questions.length + 1

  for (let index = 0; currentId && index < maxSteps; index += 1) {
    if (visited.has(currentId)) break
    visited.add(currentId)
    path.push(currentId)
    currentId = getNextQuestionId(
      currentId,
      questions,
      logicJumps,
      answers,
      hiddenFields,
      questionGroups,
    )
  }

  return path
}

function analyzeLogicWarnings(
  questions: Question[],
  questionGroups: QuestionGroupReference[],
  logicJumps: LogicJump[],
  ruleResults: SimulationRuleResult[],
  path: string[],
): LogicSimulationWarning[] {
  const warnings: LogicSimulationWarning[] = []
  const questionOrder = new Map(questions.map((question) => [question.id, question.orderIndex]))
  const groupById = new Map(questionGroups.map((group) => [group.id, group]))

  for (const jump of logicJumps) {
    if (jump.targetQuestionId && !questionOrder.has(jump.targetQuestionId)) {
      warnings.push({
        code: 'missing_target',
        label: `規則 ${jump.order + 1} 目標不存在`,
        description: '此規則指向的題目已不存在，請重新選擇跳轉目標。',
      })
    }
    if (jump.targetGroupId && !groupById.has(jump.targetGroupId)) {
      warnings.push({
        code: 'missing_target',
        label: `規則 ${jump.order + 1} 目標題組不存在`,
        description: '此規則指向的題組已不存在，請重新選擇跳轉目標。',
      })
    }
    if (jump.targetGroupId && groupById.has(jump.targetGroupId)) {
      const group = groupById.get(jump.targetGroupId)
      const hasQuestion =
        Boolean(group?.questionIds.some((questionId) => questionOrder.has(questionId))) ||
        Boolean(group?.questions?.length)
      if (!hasQuestion) {
        warnings.push({
          code: 'missing_target',
          label: `規則 ${jump.order + 1} 目標題組沒有題目`,
          description: 'Player 會改往下一個有效題目；請補題組題目或重新選擇目標。',
        })
      }
    }
    if (jump.conditions.some((condition) => !questionOrder.has(condition.field))) {
      warnings.push({
        code: 'invalid_condition',
        label: `規則 ${jump.order + 1} 有無效條件`,
        description: '此規則使用了不存在的問題或欄位，可能導致永遠不會命中。',
      })
    }
    const currentOrder = questionOrder.get(jump.questionId)
    const groupTargetOrder = jump.targetGroupId
      ? firstGroupQuestionOrder(jump.targetGroupId, questionGroups, questionOrder)
      : undefined
    const targetOrder = jump.targetQuestionId
      ? questionOrder.get(jump.targetQuestionId)
      : groupTargetOrder
    if (currentOrder !== undefined && targetOrder !== undefined && targetOrder <= currentOrder) {
      warnings.push({
        code: 'backward_jump',
        label: `規則 ${jump.order + 1} 可能造成回跳`,
        description: '目標題目在目前題目前面或相同位置，容易造成填答流程循環。',
      })
    }
  }

  const matchedByQuestion = new Map<string, number>()
  for (const result of ruleResults) {
    if (!result.matched) continue
    matchedByQuestion.set(
      result.jump.questionId,
      (matchedByQuestion.get(result.jump.questionId) ?? 0) + 1,
    )
  }
  for (const [questionId, count] of matchedByQuestion) {
    if (count > 1) {
      warnings.push({
        code: 'conflicting_rule',
        label: `${questionTitle(questionId, questions)} 有多個規則同時命中`,
        description: '系統會採用排序最前面的規則；請確認規則排序與條件是否合理。',
      })
    }
  }

  if (new Set(path).size !== path.length) {
    warnings.push({
      code: 'cycle_risk',
      label: '模擬路徑偵測到循環風險',
      description: '請檢查是否有規則跳回前面題目。',
    })
  }

  return warnings
}

function matchOutcome(
  calculationResults: CalculationResult[],
  hiddenFields: Record<string, string>,
) {
  const answers = new Map<string, AnswerValue>(
    calculationResults.map((result) => [result.key, Number(result.value)]),
  )
  const branches = createDefaultOutcomeBranches()
  return (
    branches.find((outcome) => {
      if (outcome.conditions.length === 0) return false
      const results = outcome.conditions.map((condition) =>
        evaluateCondition(condition, answers, hiddenFields),
      )
      return outcome.operator === 'AND' ? results.every(Boolean) : results.some(Boolean)
    }) ?? branches[branches.length - 1]
  )
}

function defaultAnswerForQuestion(question: Question) {
  if (question.choices[0]) return question.choices[0].value
  if (question.type === 'YES_NO') return 'true'
  if (['NUMBER', 'NUMBER_SLIDER', 'TOTAL', 'NPS', 'RATING'].includes(question.type)) return '10'
  return 'demo'
}

function parseAnswer(question: Question, value: string): AnswerValue {
  if (question.type === 'CHECKBOXES' || question.settings.allowMultiple) {
    return value ? [value] : []
  }
  if (['NUMBER', 'NUMBER_SLIDER', 'TOTAL', 'NPS', 'RATING'].includes(question.type)) {
    return Number(value)
  }
  if (question.type === 'YES_NO') return value === 'true'
  return value
}

function questionTitle(questionId: string, questions: Question[]) {
  return questions.find((question) => question.id === questionId)?.title ?? questionId
}

function jumpTargetTitle(
  jump: LogicJump,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
) {
  if (jump.targetGroupId) {
    const group = questionGroups.find((item) => item.id === jump.targetGroupId)
    return group ? `題組：${group.title ?? group.id}` : '題組不存在'
  }
  return jump.targetQuestionId ? questionTitle(jump.targetQuestionId, questions) : '結束問卷'
}

function firstGroupQuestionOrder(
  groupId: string,
  questionGroups: QuestionGroupReference[],
  questionOrder: Map<string, number>,
) {
  const group = questionGroups.find((item) => item.id === groupId)
  const explicitOrder = group?.questionIds
    .map((questionId) => questionOrder.get(questionId))
    .find((order) => order !== undefined)
  if (explicitOrder !== undefined) return explicitOrder
  return group?.questions
    ?.map((question) => questionOrder.get(question.id))
    .find((order) => order !== undefined)
}
